/**
 * Khaime Pricing Component
 * Renders subscription pricing cards with frequency toggle and currency selector
 */

import { askForCustomer } from '../checkout/customer-step';
import { KhaimeBaseElement } from '../base';
import { PRICING_STYLES } from './styles';
import { escapeHtml } from '../../utils/dom';
import { createKhaimeEvent } from '../../types/events';
import { formatPrice } from '../../types/product';
import { mountCustomSelect } from '../shared/custom-select';
import { flagUrl } from '../shared/flag';
import type {
  Product,
  SubscriptionFrequencyOption,
  BootstrapResponse,
  PricingPlanConfig,
  PricingFreePlan,
} from '../../types';

interface PlanCardData {
  product: Product;
  config: PricingPlanConfig;
}

const CHECK_ICON = `<svg class="ks-pricing-card__feature-icon" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>`;

export class KhaimePricingElement extends KhaimeBaseElement {
  static tagName = 'khaime-pricing';

  // State
  private _products: Product[] = [];
  private _bootstrap: BootstrapResponse | null = null;
  private _selectedFrequency = '';
  private _currency = '';
  private _frequencies: SubscriptionFrequencyOption[] = [];
  private _planConfigs: Map<number, PricingPlanConfig> = new Map();
  private _freePlan: PricingFreePlan | null = null;
  private _checkoutLoading: number | null = null; // product ID currently checking out
  private _stylesInjected = false;
  private _planConfigsParsed = false;
  private _fetchId = 0;

  static get observedAttributes(): string[] {
    return [
      ...super.observedAttributes,
      'plan-ids',
      'highlight-plan',
    ];
  }

  get planIds(): string {
    return this.getAttribute('plan-ids') || '';
  }

  protected getSDKType(): string {
    return 'pricing';
  }

  /**
   * Parse plan configs from data attribute (set by sdk.ts)
   */
  private parsePlanConfigs(): void {
    const raw = this.getAttribute('data-plan-configs');
    if (raw) {
      try {
        const configs: PricingPlanConfig[] = JSON.parse(raw);
        configs.forEach((c) => this._planConfigs.set(c.productId, c));
      } catch {
        // ignore parse errors
      }
    }

    const freePlanRaw = this.getAttribute('data-free-plan');
    if (freePlanRaw) {
      try {
        this._freePlan = JSON.parse(freePlanRaw);
      } catch {
        // ignore
      }
    }
  }

  /**
   * Derive unique frequencies from all products' subscription options
   */
  private deriveFrequencies(): void {
    const freqMap = new Map<string, SubscriptionFrequencyOption>();
    let defaultFreq = '';

    for (const product of this._products) {
      const opts = product.subscription_options;
      if (!opts?.pricing_options) continue;

      for (const opt of opts.pricing_options) {
        if (!freqMap.has(opt.value)) {
          freqMap.set(opt.value, opt);
        }
        if (opt.is_default && !defaultFreq) {
          defaultFreq = opt.value;
        }
      }
    }

    this._frequencies = Array.from(freqMap.values());

    // Set selected frequency to the default, or first available
    if (!this._selectedFrequency || !freqMap.has(this._selectedFrequency)) {
      this._selectedFrequency = defaultFreq || this._frequencies[0]?.value || '';
    }
  }

  /**
   * Get the display label for a frequency value.
   * Strips discount text like " - 15% off" for cleaner toggle labels.
   */
  private getFrequencyLabel(freq: SubscriptionFrequencyOption): string {
    // Clean up label: remove discount info for toggle display
    const label = freq.label || freq.value;
    const dashIndex = label.indexOf(' - ');
    return dashIndex > 0 ? label.substring(0, dashIndex) : label;
  }

  /**
   * Get price info for a product at the current frequency
   */
  private getPriceForFrequency(product: Product): {
    price: number | null;
    originalPrice: number | null;
    discount: number;
    frequencyLabel: string;
  } {
    const opts = product.subscription_options;
    if (!opts?.pricing_options) {
      return { price: product.price, originalPrice: null, discount: 0, frequencyLabel: '' };
    }

    const pricingOpt = opts.pricing_options.find((p) => p.value === this._selectedFrequency);
    if (!pricingOpt) {
      // Fallback to first option
      const first = opts.pricing_options[0];
      return {
        price: first?.final_price ?? first?.original_price ?? product.price,
        originalPrice: first?.discount > 0 ? first?.original_price ?? null : null,
        discount: first?.discount ?? 0,
        frequencyLabel: first?.label ?? '',
      };
    }

    return {
      price: pricingOpt.final_price ?? pricingOpt.original_price ?? product.price,
      originalPrice: pricingOpt.discount > 0 ? pricingOpt.original_price ?? null : null,
      discount: pricingOpt.discount,
      frequencyLabel: pricingOpt.label ?? '',
    };
  }

  /**
   * Get features for a plan card
   */
  private getFeatures(product: Product, config: PricingPlanConfig): string[] {
    // WP-provided features take priority (comma-separated string)
    if (config.features) {
      return config.features.split(',').map((f) => f.trim()).filter(Boolean);
    }

    // Fall back to API plan features
    const plan = product.subscription_options?.plans?.[0];
    if (plan?.features?.length) {
      return plan.features;
    }

    return [];
  }

  /**
   * Get plan display name
   */
  private getPlanName(product: Product): string {
    const plan = product.subscription_options?.plans?.[0];
    return plan?.display_name || product.title;
  }

  /**
   * Get plan description
   */
  private getPlanDescription(product: Product): string {
    const plan = product.subscription_options?.plans?.[0];
    return plan?.description || '';
  }

  /**
   * Core render
   */
  protected async render(): Promise<void> {
    if (!this._stylesInjected) {
      this.injectStyles(PRICING_STYLES);
      this._stylesInjected = true;
    }

    if (!this._planConfigsParsed) {
      this.parsePlanConfigs();
      this._planConfigsParsed = true;
    }

    // Show skeleton while loading
    if (!this._products.length && !this._error) {
      this.renderSkeleton();
      await this.loadData();
      // Don't call attachListeners here — base connectedCallback
      // calls setupEventListeners() after render() resolves.
      return;
    }

    this.renderPricingTable();
    // Don't call attachListeners here — base connectedCallback handles it.
  }

  /**
   * Fetch data from API
   */
  private async loadData(): Promise<void> {
    if (!this.api) return;

    const fetchId = ++this._fetchId;

    try {
      const planIdsCsv = this.planIds;
      if (!planIdsCsv) {
        this._error = 'No plan IDs specified';
        this.renderError(this._error);
        return;
      }

      // Fetch bootstrap and products in parallel
      const [bootstrap, productsResponse] = await Promise.all([
        this.api.bootstrap(),
        this.api.getProducts({
          productIds: planIdsCsv,
          currency: this._currency || undefined,
        }),
      ]);

      // A newer fetch was started — discard this stale result
      if (fetchId !== this._fetchId) return;

      this._bootstrap = bootstrap;
      this._products = productsResponse.products;

      // Set initial currency from bootstrap
      if (!this._currency) {
        this._currency = bootstrap.detectedCurrency || bootstrap.defaultCurrency || 'USD';
      }

      // Derive frequency options from products
      this.deriveFrequencies();

      // Render the pricing table and attach listeners.
      // On initial render, base connectedCallback also calls setupEventListeners()
      // after render() resolves, but by then innerHTML has already been replaced
      // so the duplicate attach is harmless (old DOM+listeners are gone).
      // On currency-change re-fetches this is the only call path.
      this.renderPricingTable();
      this.attachListeners();
    } catch (error) {
      // Ignore errors from stale fetches
      if (fetchId !== this._fetchId) return;

      this._error = error instanceof Error ? error.message : 'Failed to load pricing data';
      this.renderError(this._error);
    }
  }

  /**
   * Render skeleton loading state
   */
  private renderSkeleton(): void {
    const planCount = this.planIds.split(',').filter(Boolean).length || 2;
    const skeletonCards = Array(planCount)
      .fill(0)
      .map(
        () => `
        <div class="ks-pricing-card ks-card--elevated ks-pricing-card--skeleton">
          <div class="ks-skeleton ks-skeleton-block ks-skeleton-block--title"></div>
          <div class="ks-skeleton ks-skeleton-block ks-skeleton-block--price"></div>
          <div style="margin-top: 1.5rem;">
            <div class="ks-skeleton ks-skeleton-block ks-skeleton-block--feature"></div>
            <div class="ks-skeleton ks-skeleton-block ks-skeleton-block--feature"></div>
            <div class="ks-skeleton ks-skeleton-block ks-skeleton-block--feature"></div>
            <div class="ks-skeleton ks-skeleton-block ks-skeleton-block--feature"></div>
          </div>
          <div class="ks-skeleton ks-skeleton-block ks-skeleton-block--button" style="margin-top: auto;"></div>
        </div>
      `
      )
      .join('');

    const container = this.shadow.querySelector('.ks-container');
    const html = `
      <div class="ks-pricing" role="status" aria-busy="true" aria-label="Loading pricing plans">
        ${this.renderHeader()}
        <div class="ks-pricing__grid">
          ${skeletonCards}
        </div>
      </div>
    `;

    if (container) {
      container.innerHTML = html;
    } else {
      // Remove existing non-style content
      const styles = Array.from(this.shadow.querySelectorAll('style'));
      this.shadow.innerHTML = '';
      styles.forEach((s) => this.shadow.appendChild(s));
      const div = document.createElement('div');
      div.className = 'ks-container';
      div.innerHTML = html;
      this.shadow.appendChild(div);
    }
  }

  /**
   * Render the full pricing table
   */
  private renderPricingTable(): void {
    const cardStyle = this.config.layout?.cardStyle || 'elevated';

    // Build plan cards data in the order of planIds (preserving WordPress order)
    const planIdOrder = this.planIds.split(',').map((id) => parseInt(id.trim(), 10));
    const planCards: PlanCardData[] = [];

    for (const pid of planIdOrder) {
      const product = this._products.find((p) => p.id === pid);
      if (!product) continue;

      const config = this._planConfigs.get(pid) || { productId: pid };
      planCards.push({ product, config });
    }

    // Determine column count
    let totalCards = planCards.length;
    if (this._freePlan) totalCards++;
    const columns = Math.min(totalCards, this.config.layout?.columns || 3);

    const html = `
      <div class="ks-pricing" part="container">
        ${this.renderHeader()}
        <div class="ks-pricing__grid" part="grid" style="--ks-columns: ${columns};" aria-live="polite">
          ${this._freePlan ? this.renderFreePlanCard(cardStyle) : ''}
          ${planCards.map((card) => this.renderPlanCard(card, cardStyle)).join('')}
        </div>
      </div>
    `;

    const container = this.shadow.querySelector('.ks-container');
    if (container) {
      container.innerHTML = html;
    } else {
      const styles = Array.from(this.shadow.querySelectorAll('style'));
      this.shadow.innerHTML = '';
      styles.forEach((s) => this.shadow.appendChild(s));
      const div = document.createElement('div');
      div.className = 'ks-container';
      div.innerHTML = html;
      this.shadow.appendChild(div);
    }
  }

  /**
   * Render header with frequency toggle and currency selector
   */
  private renderHeader(): string {
    const toggleHtml = this._frequencies.length > 1
      ? `
        <div class="ks-pricing__toggle" part="toggle" role="radiogroup" aria-label="Billing frequency">
          ${this._frequencies
            .map(
              (freq) => {
                const isActive = freq.value === this._selectedFrequency;
                return `
              <button
                class="ks-pricing__toggle-btn ${isActive ? 'ks-pricing__toggle-btn--active' : ''}"
                data-frequency="${escapeHtml(freq.value)}"
                role="radio"
                aria-checked="${isActive}"
              >
                ${escapeHtml(this.getFrequencyLabel(freq))}
              </button>
            `;
              }
            )
            .join('')}
        </div>
      `
      : '';

    const currencyHtml = this._bootstrap && this._bootstrap.currencies.length > 1
      ? `<div class="ks-pricing__currency" part="currency"></div>`
      : '';

    if (!toggleHtml && !currencyHtml) return '';

    return `
      <div class="ks-pricing__header" part="header">
        ${toggleHtml}
        ${currencyHtml}
      </div>
    `;
  }

  /**
   * Render a single plan card
   */
  private renderPlanCard(card: PlanCardData, cardStyle: string): string {
    const { product, config } = card;
    const isHighlighted = config.highlight === true;
    const priceInfo = this.getPriceForFrequency(product);
    const features = this.getFeatures(product, config);
    const benefits = product.subscription_options?.benefits || [];
    const planName = this.getPlanName(product);
    const planDesc = this.getPlanDescription(product);
    const buttonLabel = config.buttonLabel || 'Get Started';
    const isLoading = this._checkoutLoading === product.id;
    const trial = product.subscription_options?.trial_available
      ? product.subscription_options
      : null;

    const highlightClass = isHighlighted ? 'ks-pricing-card--highlighted' : '';
    const ctaClass = isHighlighted ? 'ks-pricing-card__cta--primary' : 'ks-pricing-card__cta--secondary';

    return `
      <div class="ks-pricing-card ks-card--${cardStyle} ${highlightClass}" data-product-id="${product.id}" part="card">
        ${isHighlighted ? '<div class="ks-pricing-card__badge">Popular</div>' : ''}
        <h3 class="ks-pricing-card__name">${escapeHtml(planName)}</h3>
        ${planDesc ? `<p class="ks-pricing-card__description">${escapeHtml(planDesc)}</p>` : ''}
        <div class="ks-pricing-card__price">
          <span class="ks-pricing-card__amount">${(priceInfo.price ?? 0) === 0 ? 'Free' : formatPrice((priceInfo.price ?? 0) / 100, this._currency)}</span>
          ${(priceInfo.price ?? 0) > 0 ? `<span class="ks-pricing-card__frequency">/${escapeHtml(this.getSelectedFrequencyShortLabel())}</span>` : ''}
          ${
            priceInfo.originalPrice && priceInfo.discount > 0
              ? `
                <div>
                  <span class="ks-pricing-card__original-price">${formatPrice((priceInfo.originalPrice ?? 0) / 100, this._currency)}</span>
                  <span class="ks-pricing-card__discount">Save ${priceInfo.discount}%</span>
                </div>
              `
              : ''
          }
          ${trial?.trial_available && trial.trial_duration_days ? `<div class="ks-pricing-card__trial">${trial.trial_duration_days}-day free trial</div>` : ''}
        </div>
        <hr class="ks-pricing-card__divider" />
        ${
          features.length
            ? `
              <ul class="ks-pricing-card__features">
                ${features.map((f) => `<li class="ks-pricing-card__feature">${CHECK_ICON}<span>${escapeHtml(f)}</span></li>`).join('')}
              </ul>
            `
            : ''
        }
        ${
          benefits.length
            ? `
              <div class="ks-pricing-card__benefits">
                ${benefits.map((b) => `<div class="ks-pricing-card__benefit"><span class="ks-pricing-card__benefit-icon">&#10003;</span> ${escapeHtml(b)}</div>`).join('')}
              </div>
            `
            : ''
        }
        <button
          class="ks-pricing-card__cta ${ctaClass}"
          data-product-id="${product.id}"
          ${isLoading ? 'disabled' : ''}
        >
          ${isLoading ? '<div class="ks-spinner"></div>' : ''}${escapeHtml(buttonLabel)}
        </button>
      </div>
    `;
  }

  /**
   * Render free plan card
   */
  private renderFreePlanCard(cardStyle: string): string {
    if (!this._freePlan) return '';

    const features = this._freePlan.features
      ? this._freePlan.features.split(',').map((f) => f.trim()).filter(Boolean)
      : [];

    return `
      <div class="ks-pricing-card ks-card--${cardStyle} ks-pricing-card--free" part="card-free">
        <h3 class="ks-pricing-card__name">${escapeHtml(this._freePlan.label)}</h3>
        <div class="ks-pricing-card__price">
          <span class="ks-pricing-card__amount">Free</span>
        </div>
        <hr class="ks-pricing-card__divider" />
        ${
          features.length
            ? `
              <ul class="ks-pricing-card__features">
                ${features.map((f) => `<li class="ks-pricing-card__feature">${CHECK_ICON}<span>${escapeHtml(f)}</span></li>`).join('')}
              </ul>
            `
            : ''
        }
        <a
          href="${escapeHtml(this._freePlan.buttonUrl || '#')}"
          class="ks-pricing-card__cta ks-pricing-card__cta--secondary"
          data-free-plan="true"
        >
          ${escapeHtml(this._freePlan.buttonLabel || 'Start Free')}
        </a>
      </div>
    `;
  }

  /**
   * Get a short label for the selected frequency (for "/ month" display).
   * Matches on the structured `value` field first (e.g. "monthly", "every_6_months"),
   * falling back to the display label.
   */
  private getSelectedFrequencyShortLabel(): string {
    const freq = this._frequencies.find((f) => f.value === this._selectedFrequency);
    if (!freq) return 'month';

    const value = freq.value.toLowerCase();

    // Match structured value patterns first
    if (/^monthly$|_month$|_1_month/.test(value)) return 'mo';
    if (/yearly|annual|_12_month|_1_year/.test(value)) return 'yr';
    if (/weekly|_week/.test(value)) return 'wk';
    if (/semi_annual|_6_month|every_6/.test(value)) return '6 mo';
    if (/quarterly|_3_month|every_3/.test(value)) return '3 mo';
    if (/bi_weekly|every_2_week/.test(value)) return '2 wk';

    // Fallback to cleaned display label
    return this.getFrequencyLabel(freq).toLowerCase();
  }

  /**
   * Called by base class after render(). Delegates to attachListeners().
   */
  protected setupEventListeners(): void {
    this.attachListeners();
  }

  /**
   * Attach event listeners to current DOM.
   * Called by setupEventListeners (base lifecycle) and after internal re-renders.
   * Safe to call repeatedly — innerHTML re-renders destroy old DOM+listeners.
   */
  private attachListeners(): void {
    // Frequency toggle buttons
    this.shadow.querySelectorAll('.ks-pricing__toggle-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const frequency = target.dataset.frequency;
        if (frequency && frequency !== this._selectedFrequency) {
          this._selectedFrequency = frequency;
          this.renderPricingTable();
          this.attachListeners();
        }
      });
    });

    // Currency selector
    const currencyHost = this.shadow.querySelector('.ks-pricing__currency') as HTMLElement | null;
    if (currencyHost && this._bootstrap && this._bootstrap.currencies.length > 1) {
      mountCustomSelect(currencyHost, {
        options: this._bootstrap.currencies.map((c) => ({
          value: c.code,
          label: `${c.symbol} ${c.code}`.trim(),
          iconUrl: flagUrl(c.countryCode, c.code),
          iconAlt: `${c.code} flag`,
        })),
        value: this._currency,
        ariaLabel: 'Currency',
        onChange: (newCurrency) => {
          if (newCurrency === this._currency) return;
          const previousCurrency = this._currency;
          this._currency = newCurrency;

          this.dispatchEvent(
            createKhaimeEvent('khaime:currency-change', {
              previousCurrency,
              newCurrency,
              merchantId: this.merchantId,
            })
          );

          // Refetch products with new currency
          this._products = [];
          this.renderSkeleton();
          this.attachListeners(); // keep dropdown interactive during load
          this.loadData();
        },
      });
    }

    // CTA buttons
    this.shadow.querySelectorAll('.ks-pricing-card__cta[data-product-id]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const productId = parseInt(target.dataset.productId || '0', 10);
        if (productId) {
          this.handlePlanSelect(productId);
        }
      });
    });
  }

  /**
   * Handle plan selection — dispatch event and initiate checkout
   */
  private async handlePlanSelect(productId: number): Promise<void> {
    const product = this._products.find((p) => p.id === productId);
    if (!product || !this.api) return;

    const priceInfo = this.getPriceForFrequency(product);
    const planKey = product.subscription_options?.plans?.[0]?.plan_key;

    // Dispatch plan-selected event
    this.dispatchEvent(
      createKhaimeEvent('khaime:plan-selected', {
        product,
        planKey,
        frequency: this._selectedFrequency,
        price: priceInfo.price ?? 0,
        currency: this._currency,
        merchantId: this.merchantId,
      })
    );

    // Initiate checkout
    this._checkoutLoading = productId;
    this.renderPricingTable();
    this.attachListeners();

    try {
      const customer = await askForCustomer();
      if (!customer) return;
      const session = await this.api.initCheckout({
        productId,
        currency: this._currency,
        subscriptionFrequencyKey: this._selectedFrequency || undefined,
        customerEmail: customer.email,
        customerName: customer.name,
      });

      // Lazy-load and open checkout modal
      const { openCheckoutModal } = await import('../checkout/checkout-modal');
      openCheckoutModal({
        merchantId: this.merchantId,
        sessionToken: session.sessionToken,
        onSuccess: (result) => {
          this.dispatchEvent(
            createKhaimeEvent('khaime:payment-success', {
              ...result,
              merchantId: this.merchantId,
            })
          );
        },
        onError: (error) => {
          this.dispatchEvent(
            createKhaimeEvent('khaime:payment-error', {
              error: error.message,
              merchantId: this.merchantId,
            })
          );
        },
        onClose: () => {},
      });
    } catch (error) {
      this.dispatchEvent(
        createKhaimeEvent('khaime:payment-error', {
          error: error instanceof Error ? error.message : 'Checkout failed',
          merchantId: this.merchantId,
        })
      );
    } finally {
      this._checkoutLoading = null;
      this.renderPricingTable();
      this.attachListeners();
    }
  }

  /**
   * Cleanup
   */
  protected cleanup(): void {
    this._products = [];
    this._bootstrap = null;
    this._planConfigs.clear();
  }
}

export default KhaimePricingElement;
